import { useCallback, useMemo, useRef, useState } from "react";
import { auth } from "../lib/firebase";
import { authenticatedFetch } from "../lib/api";
import { RiskReport } from "../types";

// Browser Risk Report data hook (ENG-2201). Admin-only: fetches the caller's own tenant report
// from GET /business/risk-report (tenant resolved server-side from the Firebase token; the window
// is a clamped query param). Fails honest — on error it surfaces a message and clears the report
// rather than fabricating data (docs/HAVEN_BUSINESS_REPORTING.md §6).
export function useRiskReport() {
  const [report, setReport] = useState<RiskReport | null>(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);
  // The (window, group) this report was last loaded for (ENG-2563). Hoisted state persists across page
  // navigation, so a re-mount's fetch is a no-op when nothing changed - the Risk Report no longer
  // re-loads behind a spinner every time you return to it. Set only on success, so a failed fetch
  // retries on the next visit. Mirrors useTelemetryEvents' loadedKey.
  // ENG-2722: a ref, not state — nothing renders from it, and as state it forced `fetchReport` to
  // be rebuilt on every load, which is what made RiskReportPage's load effect need a suppression.
  const loadedKey = useRef<string | null>(null);
  // Report generation runs several BigQuery queries, so a cold fetch takes seconds. Guard against
  // out-of-order responses when the admin toggles 14 ↔ 30 days: only the LATEST request may apply —
  // a stale response would otherwise print the wrong window's report.
  const requestSeq = useRef(0);
  // ENG-2965: the key currently being FETCHED, which `loadedKey` cannot express — it is written on
  // completion, so an in-flight request never reserved its own key. Two opposite bugs came out of
  // that one gap, and both were reported as "the 14-day toggle does nothing":
  //
  //   * A click was SWALLOWED. `windowDays` is local state on RiskReportPage while `loadedKey` is
  //     hoisted here, so leaving the page and returning resets the toggle to 30 while the ref still
  //     reads "14|". The 30-day load then runs for 15–36s (ENG-2962), and a click on 14 during it
  //     matched the stale ref and returned early. The toggle highlighted 14 and the 30-day report
  //     landed underneath it — exactly what was reported from prod.
  //   * A click was DUPLICATED. Double-clicking one window issued two identical multi-second
  //     reports, because nothing recorded that the first was already on its way.
  //
  // So the guard has to compare against where we are HEADED, not only where we have been.
  const pendingKey = useRef<string | null>(null);
  // ENG-3366: bumped by `invalidate`. A response to a request made before the bump was built from the
  // policy or membership as it was then, so it is shown but not remembered as current.
  const generation = useRef(0);

  const fetchReport = useCallback(async (windowDays: number, groupId: string | null = null) => {
    if (!auth.currentUser) return;
    const key = `${windowDays}|${groupId ?? ""}`;
    // Skip only when this exact window+group is what we are already showing or already loading.
    // `pendingKey` wins while a fetch is open: whatever `loadedKey` holds is being replaced.
    if ((pendingKey.current ?? loadedKey.current) === key) return; // no spinner, no round-trip
    const seq = ++requestSeq.current;
    const requestGeneration = generation.current;
    pendingKey.current = key;
    setLoadingReport(true);
    setReportError(null);
    try {
      const qs = new URLSearchParams({ window: String(windowDays) });
      if (groupId) qs.set("groupId", groupId);
      const response = await authenticatedFetch(`/business/risk-report?${qs.toString()}`);
      if (seq !== requestSeq.current) return; // superseded by a newer window selection
      if (response.ok) {
        // ENG-2965: parse first, then re-check. `response.json()` is a SECOND suspension point, so
        // the check above cannot be the only one — a request superseded while its body was being
        // parsed still reached setReport and still wrote its own key, which is precisely the
        // out-of-order apply the seq counter exists to prevent. The guard was written once and
        // awaited twice.
        const body = await response.json();
        if (seq !== requestSeq.current) return;
        setReport(body);
        loadedKey.current = requestGeneration === generation.current ? key : null;
      } else {
        setReport(null);
        loadedKey.current = null;
        setReportError("Could not generate the risk report for your organization.");
      }
    } catch (err) {
      if (seq !== requestSeq.current) return;
      console.error("Error fetching risk report:", err);
      setReport(null);
      loadedKey.current = null;
      setReportError("Could not generate the risk report for your organization.");
    } finally {
      // Only the newest request may clear the reservation — a superseded one returning late must not
      // reopen the key its successor is still fetching.
      if (seq === requestSeq.current) {
        pendingKey.current = null;
        setLoadingReport(false);
      }
    }
  }, []);

  const reset = useCallback(() => {
    setReport(null);
    setReportError(null);
    loadedKey.current = null;
    // Drop the reservation too, or a reset mid-flight (logout, tenant switch) would leave the next
    // caller skipping the window it just asked for.
    pendingKey.current = null;
  }, []);

  /**
   * ENG-3366: the org's policy or membership changed, so the next visit fetches instead of reusing the
   * report it holds, which still says what the lists said when it loaded. The report stays on screen
   * until then. Called by the console's policy notifier (every allow, deny, group-list and enforcement
   * write) and after membership actions; the backend keys its own cache on the same things.
   */
  const invalidate = useCallback(() => {
    loadedKey.current = null;
    pendingKey.current = null;
    generation.current += 1;
  }, []);

  /**
   * ENG-3467: "Regenerate report". The page promised a regenerate for a report with unavailable
   * sections, but a same-window fetch is skipped by the `loadedKey` guard, so nothing could do it. The
   * backend never caches a degraded report (`shouldCache` in RiskReport.kt), so a refetch rebuilds it.
   *
   * A no-op while any report is loading: a double click, or a click during a window change, must not
   * start a second multi-second build.
   */
  const regenerate = useCallback((windowDays: number, groupId: string | null = null) => {
    if (pendingKey.current !== null) return Promise.resolve();
    loadedKey.current = null;
    return fetchReport(windowDays, groupId);
  }, [fetchReport]);

  return useMemo(
    () => ({ report, loadingReport, reportError, fetchReport, reset, invalidate, regenerate }),
    [report, loadingReport, reportError, fetchReport, reset, invalidate, regenerate],
  );
}
